/**
 * Error message mapper for converting backend error codes to user-friendly messages.
 * Maps error codes from the backend ErrorMessages enum to frontend display messages.
 */

/**
 * Map of backend error codes to user-friendly error messages
 */
export const ERROR_MESSAGES = {
  // Authentication & Authorization Errors
  INVALID_CREDENTIALS: "Invalid username or password. Please try again.",
  EMAIL_NOT_VERIFIED:
    "Please verify your email before logging in. Check your inbox for the verification link.",
  TOKEN_EXPIRED:
    "Your verification link has expired. Please request a new one.",
  INVALID_TOKEN:
    "Invalid verification code. Please check the link and try again.",
  INVALID_JWT: "Your session has expired. Please log in again.",
  MALFORMED_JWT: "Authentication error. Please log in again.",

  // User Registration Errors
  USER_ALREADY_EXIST:
    "This username is already taken. Please choose another one.",
  ACCOUNT_ALREADY_EXISTS: "An account with this email already exists.",
  NULL_ITEM_IN_SIGN_UP: "Please fill in all required fields.",
  INCORRECT_EMAIL_FORMAT: "Please enter a valid email address.",
  INVALID_PASSWORD: "Password must be at least 6 characters long.",

  // Meal Errors
  MEAL_DOES_NOT_EXIST: "Meal not found.",
  MEAL_ALREADY_EXISTS: "A meal with this name already exists.",
  DOES_NOT_BELONG_TO_USER:
    "You do not have permission to access this resource.",
  INVALID_MEAL_DATA: "Invalid meal data provided. Please check your input.",

  // Meal Plan Errors
  MEAL_PLAN_ENTRY_NOT_FOUND: "Meal plan entry not found.",
  INVALID_DAY_CODE: "Invalid day selected.",
  INVALID_MEAL_CODE: "Invalid meal type selected.",

  // Shopping List Errors
  SHOPPING_LIST_ITEM_NOT_FOUND: "Shopping list item not found.",
  INVALID_SHOPPING_LIST_DATA: "Invalid shopping list data.",

  // Ingredient Unit Errors
  INGREDIENT_UNIT_NOT_FOUND: "Ingredient unit not found.",
  INGREDIENT_UNIT_IN_USE:
    "This unit is currently used by an ingredient and cannot be deleted.",

  // Validation Errors
  INVALID_REQUEST: "Invalid request. Please check your input.",
  PATH_VARIABLE_MISMATCH: "Invalid parameter provided.",
  MISSING_REQUIRED_FIELD: "Required field is missing.",

  // Server Errors
  UNEXPECTED_ERROR: "An unexpected error occurred. Please try again later.",
  SERVER_ERROR: "Server error. Please try again later.",
  DATABASE_ERROR: "Database error. Please try again later.",

  // Generic fallback
  UNKNOWN_ERROR: "An error occurred. Please try again.",
};

/**
 * Get a user-friendly error message for a given error code
 *
 * @param {string} errorCode - The error code from the backend
 * @param {string} defaultMessage - Default message if no mapping exists
 * @returns {string} - User-friendly error message
 */
export function getUserFriendlyMessage(errorCode, defaultMessage = null) {
  if (!errorCode) {
    return defaultMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  // Try to get mapped message
  const mappedMessage = ERROR_MESSAGES[errorCode];

  if (mappedMessage) {
    return mappedMessage;
  }

  // Fall back to default message or unknown error
  return defaultMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Get error message from an error response object
 * Tries multiple strategies to extract a meaningful error message
 *
 * @param {object} error - The error object from axios or other source
 * @returns {string} - User-friendly error message
 */
export function getErrorMessage(error) {
  // Network error (no response from server)
  if (!error.response) {
    return "Unable to connect to server. Please check your internet connection.";
  }

  const { status, data } = error.response;

  // Try to get error code from response
  const errorCode = data?.error;

  // Try to get message from response (might already be user-friendly from backend)
  const backendMessage = data?.message;

  // Get user-friendly message based on error code
  if (errorCode) {
    return getUserFriendlyMessage(errorCode, backendMessage);
  }

  // If no error code, use backend message if available
  if (backendMessage) {
    return backendMessage;
  }

  // Fall back to HTTP status-based messages
  switch (status) {
    case 400:
      return "Invalid request. Please check your input.";
    case 401:
      return "Session expired. Please log in again.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested resource was not found.";
    case 409:
      return "This item already exists.";
    case 500:
      return "Server error. Please try again later.";
    default:
      return ERROR_MESSAGES.UNEXPECTED_ERROR;
  }
}

const errorMessages = {
  ERROR_MESSAGES,
  getUserFriendlyMessage,
  getErrorMessage,
};

export default errorMessages;
